import { FC, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { ColorSwatchGrid } from "~/features/settings/components/categories/ColorPicker";
import { cn } from "~/lib/utils";
import type { BatesStampPosition } from "~/types/bates";

const POSITION_MESSAGES = defineMessages({
  TOP_LEFT: {
    id: "settings.batesNumbering.position.TOP_LEFT",
    defaultMessage: "Top Left",
  },
  TOP_CENTER: {
    id: "settings.batesNumbering.position.TOP_CENTER",
    defaultMessage: "Top Center",
  },
  TOP_RIGHT: {
    id: "settings.batesNumbering.position.TOP_RIGHT",
    defaultMessage: "Top Right",
  },
  BOTTOM_LEFT: {
    id: "settings.batesNumbering.position.BOTTOM_LEFT",
    defaultMessage: "Bottom Left",
  },
  BOTTOM_CENTER: {
    id: "settings.batesNumbering.position.BOTTOM_CENTER",
    defaultMessage: "Bottom Center",
  },
  BOTTOM_RIGHT: {
    id: "settings.batesNumbering.position.BOTTOM_RIGHT",
    defaultMessage: "Bottom Right",
  },
});

const STAMP_POSITION_ARIA = defineMessages({
  groupLabel: {
    id: "settings.batesNumbering.stampPositionGroup",
    defaultMessage: "Stamp position",
  },
  changeColour: {
    id: "settings.batesNumbering.changeColour",
    defaultMessage: "{position} \u2014 click to change colour",
  },
});

/** Placement of each dot inside the schematic page (Tailwind). */
const POSITIONS: {
  key: BatesStampPosition;
  className: string;
}[] = [
  { key: "TOP_LEFT", className: "top-2 left-2" },
  { key: "TOP_CENTER", className: "top-2 left-1/2 -translate-x-1/2" },
  { key: "TOP_RIGHT", className: "top-2 right-2" },
  { key: "BOTTOM_LEFT", className: "bottom-2 left-2" },
  { key: "BOTTOM_CENTER", className: "bottom-2 left-1/2 -translate-x-1/2" },
  { key: "BOTTOM_RIGHT", className: "bottom-2 right-2" },
];

interface BatesStampPositionPickerProps {
  position: BatesStampPosition;
  /** 6-digit hex, e.g. "#000000" */
  color: string;
  onPositionChange: (position: BatesStampPosition) => void;
  onColorChange: (hex: string) => void;
}

/**
 * Schematic-page picker that merges stamp position and colour into one control.
 *
 * Six dots sit on a miniature page. Clicking an inactive dot moves the stamp
 * to that position; clicking the active (coloured) dot opens a print-safe
 * colour palette popover.
 */
export const BatesStampPositionPicker: FC<BatesStampPositionPickerProps> = ({
  position,
  color,
  onPositionChange,
  onColorChange,
}) => {
  const intl = useIntl();
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div
      role="radiogroup"
      aria-label={intl.formatMessage(STAMP_POSITION_ARIA.groupLabel)}
      className="relative h-[220px] w-[170px] rounded-lg border border-border bg-white shadow-xs"
    >
      {/* Schematic document body lines */}
      <div className="pointer-events-none absolute inset-x-6 top-1/2 -translate-y-1/2 space-y-2">
        <div className="h-[5px] w-full rounded bg-gray-200" />
        <div className="h-[5px] w-5/6 rounded bg-gray-200" />
        <div className="h-[5px] w-11/12 rounded bg-gray-200" />
        <div className="h-[5px] w-2/3 rounded bg-gray-200" />
      </div>

      {POSITIONS.map(({ key, className }) => {
        const selected = key === position;
        const label = intl.formatMessage(POSITION_MESSAGES[key]);
        const activeLabel = intl.formatMessage(
          STAMP_POSITION_ARIA.changeColour,
          { position: label }
        );

        const dotButton = (
          <button
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={selected ? activeLabel : label}
            title={selected ? activeLabel : label}
            onClick={selected ? undefined : () => onPositionChange(key)}
            className={cn(
              "absolute z-5 flex h-8 w-8 items-center justify-center rounded-md transition-colors",
              "hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
              className
            )}
          >
            <span
              className="rounded-full transition-all"
              style={
                selected
                  ? {
                      width: 20,
                      height: 20,
                      backgroundColor: color,
                      boxShadow: `0 0 0 3px ${color}38`,
                    }
                  : {
                      width: 6,
                      height: 6,
                      backgroundColor: "#9ca3af",
                    }
              }
            />
          </button>
        );

        if (!selected) {
          return <span key={key}>{dotButton}</span>;
        }

        return (
          <Popover key={key} open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>{dotButton}</PopoverTrigger>
            <PopoverContent
              side="right"
              align="start"
              sideOffset={8}
              className="w-auto p-0 overflow-hidden"
            >
              <ColorSwatchGrid
                mode="print-safe"
                value={color}
                onSelect={(hex) => {
                  onColorChange(hex);
                  setPickerOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>
        );
      })}
    </div>
  );
};
